import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { faLinkedin, faFacebook, faInstagram, faYoutube, faTiktok } from "@fortawesome/free-brands-svg-icons";
import { formatDate } from "@serie3/common/Shared/timeingStuff";
import Interactions from "../../shared/Interactions";
import { useTranslation } from "react-i18next";
import YoutubeIcon from "../../shared/YoutubeIcon";
import GroupLogo from "../../shared/GroupLogo";

const groupInitials = (name) => {
  const words = String(name ?? "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  return words.length > 1 ? words.slice(0, 2).map((w) => w[0].toUpperCase()).join("") : words[0].slice(0, 2).toUpperCase();
};

const PLATFORM_ICON = {
  linkedin:  { icon: faLinkedin,  color: "#0077b5" },
  facebook:  { icon: faFacebook,  color: "#1877f2" },
  instagram: { icon: faInstagram, color: "#e1306c" },
  youtube:   { icon: faYoutube,   color: "#ff0000" },
  tiktok:    { icon: faTiktok,    color: "#010101" },
};

const STATUS_COLOR = {
  proposed: { border: "#e0a800", badge: "rgba(247,204,83,0.18)", text: "#8a5a00" },
  error: { border: "#f34e4e", badge: "rgba(243,78,78,0.1)", text: "#f34e4e" },
  scheduled: { border: "#f1734f", badge: "rgba(241,115,79,0.1)", text: "#f1734f" },
  published: { border: "#51d28c", badge: "rgba(81,210,140,0.1)", text: "#2da866" },
  default: { border: "#038edc", badge: "rgba(3,142,220,0.1)", text: "#038edc" },
};

const CustomEvent = ({ event, setViewId, deletePlan }) => {
  const { t } = useTranslation();

  const statusKey = event.status === "proposed"
    ? "proposed"
    : event.status.includes("error")
    ? "error"
    : event.status.includes("scheduled")
    ? "scheduled"
    : event.status.includes("published")
    ? "published"
    : "default";

  const colors = STATUS_COLOR[statusKey];

  const Badge = ({ label, bg, color }) => (
    <span style={{
      display: "inline-block", fontSize: 10, fontWeight: 700,
      padding: "2px 8px", borderRadius: 20,
      background: bg, color,
    }}>
      {label}
    </span>
  );

  const platforms = event.platforms ?? (event.platform ? [event.platform] : ["linkedin"]);
  const multiPlatform = platforms.length > 1;

  return (
    <div style={{
      borderRadius: 8,
      borderLeft: `4px solid ${colors.border}`,
      background: "#fff",
      minHeight: 60,
      overflow: "hidden",
    }}>
      <div style={{ padding: "8px 10px" }}>
        {/* Title row */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
          {!multiPlatform && platforms[0] === "youtube" && (
            <YoutubeIcon height={20} style={{ flexShrink: 0 }} />
          )}
          {!multiPlatform && platforms[0] !== "youtube" && PLATFORM_ICON[platforms[0]] && (
            <FontAwesomeIcon
              icon={PLATFORM_ICON[platforms[0]].icon}
              style={{ fontSize: 14, color: PLATFORM_ICON[platforms[0]].color, flexShrink: 0 }}
            />
          )}
          {(event.sharedIn ?? []).map((g) => (
            <GroupLogo
              key={g.id}
              groupId={g.id}
              size={18}
              title={g.name}
              fallback={groupInitials(g.name)}
              style={{ borderRadius: 5, background: "rgba(86,74,177,0.12)", color: "#564ab1", fontSize: 8, fontWeight: 800 }}
            />
          ))}
          <strong
            onClick={() => setViewId(event)}
            style={{ fontSize: 13, color: "#111827", cursor: "pointer", lineHeight: 1.3, flex: 1,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {event.title}
          </strong>
          {!event.delegatedToMe && (
            <FontAwesomeIcon
              icon={faTrash}
              onClick={() => deletePlan(event)}
              style={{ fontSize: 12, color: "#f34e4e", cursor: "pointer", flexShrink: 0, opacity: 0.5 }}
              onMouseEnter={e => { e.currentTarget.style.opacity = "1"; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = "0.5"; }}
            />
          )}
        </div>

        {/* Date */}
        <div onClick={() => setViewId(event)} style={{ cursor: "pointer" }}>
          <div style={{ fontSize: 11, color: "#9299a8", marginTop: 3 }}>
            {formatDate(event.postDate)}
          </div>

          {/* Badges */}
          <div style={{ marginTop: 5, display: "flex", gap: 4, flexWrap: "wrap" }}>
            {event.status === "draft" && (
              <Badge label={t("Backend.Post.State.Draft")} bg="rgba(107,114,128,0.1)" color="#6b7280" />
            )}
            {event.publishAs && (
              <Badge
                label={`${event.delegatedToMe
                  ? `${t("Backend.Post.Schedule.PlannedBy", "planned by")} ${event.plannedBy || "?"}`
                  : `${t("Backend.Post.Schedule.For", "for")} ${event.publishAs}`}${event.approvalStatus === "proposed" ? ` · ${t("Backend.Post.State.WaitingApproval", "waiting for approval")}` : ""}`}
                bg={event.approvalStatus === "proposed" ? STATUS_COLOR.proposed.badge : "rgba(15,123,134,0.12)"}
                color={event.approvalStatus === "proposed" ? STATUS_COLOR.proposed.text : "#0b5f68"}
              />
            )}
            {event.planning && (
              !event.id
                ? <Badge label={t("Backend.Post.State.Recommendation")} bg={colors.badge} color={colors.text} />
                : <Badge label={t("Backend.Post.State.Premade")} bg="rgba(81,210,140,0.1)" color="#2da866" />
            )}
          </div>

          {multiPlatform ? (
            <div style={{ marginTop: 5, display: "flex", gap: 6 }}>
              {platforms.map((p) =>
                p === "youtube" ? (
                  <YoutubeIcon key={p} height={20} />
                ) : PLATFORM_ICON[p] ? (
                  <FontAwesomeIcon
                    key={p}
                    icon={PLATFORM_ICON[p].icon}
                    style={{ fontSize: 14, color: PLATFORM_ICON[p].color }}
                  />
                ) : null
              )}
            </div>
          ) : (
            event.status.includes("published") && (
              <div style={{ marginTop: 5 }}>
                <Interactions
                  views={event.stats?.views ?? 0}
                  likes={event.stats?.likes ?? 0}
                  comments={event.stats?.comments ?? 0}
                  size="smaller"
                />
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomEvent;
